import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { UserBrief } from "../api/client.ts";
import UserAvatar from "./UserAvatar.tsx";

interface CollaboratorStackProps {
  creator?: UserBrief | null;
  collaborators: UserBrief[];
  maxVisible?: number;
  size?: number;
}

function labelFor(u: UserBrief): string {
  return u.display_name?.trim() || u.email?.trim() || "Unknown";
}

export default function CollaboratorStack({
  creator,
  collaborators,
  maxVisible = 3,
  size = 28,
}: CollaboratorStackProps) {
  const visible = collaborators.slice(0, maxVisible);
  const overflow = collaborators.slice(maxVisible);
  const overlap = Math.max(6, Math.round(size * 0.3));

  if (!creator && visible.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
      {creator && (
        <Box sx={{ position: "relative", zIndex: visible.length + 1 }}>
          <UserAvatar
            displayName={creator.display_name}
            email={creator.email}
            pictureUrl={creator.picture_url}
            size={size}
          />
        </Box>
      )}
      {visible.map((u, idx) => (
        <Box
          key={u.user_id}
          sx={{
            position: "relative",
            ml: creator || idx > 0 ? `-${overlap}px` : 0,
            zIndex: visible.length - idx,
            "& .MuiAvatar-root": {
              border: "2px solid",
              borderColor: "background.paper",
            },
          }}
        >
          <UserAvatar
            displayName={u.display_name}
            email={u.email}
            pictureUrl={u.picture_url}
            size={size}
          />
        </Box>
      ))}
      {overflow.length > 0 && (
        <Tooltip
          title={
            <Box>
              {overflow.map((u) => (
                <Typography key={u.user_id} variant="caption" sx={{ display: "block" }}>
                  {labelFor(u)}
                </Typography>
              ))}
            </Box>
          }
          placement="top"
        >
          <Avatar
            sx={{
              width: size,
              height: size,
              fontSize: size * 0.38,
              bgcolor: "action.selected",
              color: "text.primary",
              ml: `-${overlap}px`,
              border: "2px solid",
              borderColor: "background.paper",
            }}
          >
            +{overflow.length}
          </Avatar>
        </Tooltip>
      )}
    </Box>
  );
}
